import { Component, ContentChildren, EventEmitter, Input, OnChanges, Output, QueryList, SimpleChanges, TemplateRef } from '@angular/core';

import { NamedTemplateComponent } from '../named-template/named-template.component';

/**
 * This component is used to stack multiple panels in a single component.
 */
@Component({
    selector: 'cw-panel-stack',
    templateUrl: './panel-stack.component.html',
    styleUrls: ['./panel-stack.component.scss'],
    standalone: false,
})
export class PanelStackComponent implements OnChanges {
    /**
     * The name of the active panel. As as string. Use it as double binding
     */
    @Input() activePanel = '';

    /**
     * Event emitted when the active panel changes.
     */
    @Output() activePanelChange = new EventEmitter<string>();

    @ContentChildren(NamedTemplateComponent) panelItems!: QueryList<NamedTemplateComponent>;

    get panelsRef(): TemplateRef<any>[] {
        return this.panelItems?.map((item) => item.template) ?? [];
    }

    private get panelsDefinition(): Record<string, TemplateRef<any>> {
        return this.panelItems.reduce(
            (acc, item) => {
                acc[item.name] = item.template;
                return acc;
            },
            {} as Record<string, TemplateRef<any>>
        );
    }
    private firstParentValue: string | null = null;
    private internalActivePanel: string | null = null;
    private panelsHistory: string[] = [];
    private get panelKeys(): string[] {
        return this.panelItems?.map((item) => item.name) ?? [];
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.validatePanelKey(changes['activePanel'].previousValue);
        this.validatePanelKey(changes['activePanel'].currentValue);

        if (changes['activePanel']?.previousValue) {
            const previousValue = changes['activePanel'].previousValue;
            const lastValue = this.panelsHistory[this.panelsHistory.length - 1];
            const areHistoryValuesDifferent = previousValue !== lastValue;
            const areInternaValuesDifferent = this.internalActivePanel !== changes['activePanel'].currentValue;

            const isHistoryEmpty = this.panelsHistory.length === 0;
            if (!this.firstParentValue) {
                this.firstParentValue = previousValue;
            }

            if (areInternaValuesDifferent) {
                if (!isHistoryEmpty && areHistoryValuesDifferent) {
                    this.pushHistory(previousValue);
                } else if (this.firstParentValue) {
                    this.pushHistory(this.firstParentValue);
                }
            }
        }

        if (changes['activePanel']) {
            this.emitActivePanelChange();
        }
    }

    isPanelActive(panel: TemplateRef<any>): boolean {
        return this.panelsDefinition?.[this.activePanel] === panel;
    }

    goToPreviousPanel(): void {
        if (this.hasPreviousPanel()) {
            const previousPanel = this.panelsHistory.pop();
            if (previousPanel) {
                this.internalActivePanel = previousPanel;
                this.emitActivePanelChange(this.internalActivePanel);
            }
        }
    }

    goToFirstPanel(): void {
        this.panelsHistory = [];
        this.internalActivePanel = this.firstParentValue ?? this.panelKeys[0] ?? '';
        this.emitActivePanelChange(this.internalActivePanel);
    }

    hasPreviousPanel(): boolean {
        return this.panelsHistory.length > 0;
    }

    emitActivePanelChange(panelKey?: string): void {
        this.activePanelChange.emit(panelKey ?? this.activePanel);
    }

    private pushHistory(panelKey: string): void {
        this.validatePanelKey(panelKey);
        this.panelsHistory.push(panelKey);
    }

    private validatePanelKey(panelKey: string): void {
        if (!panelKey || !this.panelKeys.length) {
            // probalby not set
            return;
        }
        if (!this.panelKeys.includes(panelKey)) {
            const validKeys = this.panelKeys.join(', ');
            throw new Error(`Panel key '${panelKey}' not assigned to any template. Valid keys: ${validKeys}.`);
        }
    }
}
